import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BreakpointObserverService, ConfigurationService, OverlayService } from '@core';
import {
  ARIA_LABEL_BY_FILTER,
  DEFAULT_FILTERS_FORM_STATE,
  FILTER_OPTIONS_SERVICE,
  FilterControlName,
  FormState,
  ID_BY_FILTER,
  LABEL_BY_FILTER,
  ProductListFilterOptionsSignal,
  ProductListFiltersForm,
  ProductListMobileTemplateType,
} from './product-filter.model';

import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NavigationMenuHeaderComponent } from '@navigation';
import { MobileOverlayComponent } from '../mobile-overlay/mobile-overlay.component';
import { IsArrayPipe } from '../pipes/is-array.pipe';
import { TagComponent } from '../tag/tag.component';
import { MobileSelectComponent } from './components';
import { merge } from 'rxjs';
import { SkeletonTemplateService } from '@skeleton-loader';

@Component({
  selector: 'lib-product-list-filters',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MobileOverlayComponent,
    NavigationMenuHeaderComponent,
    MobileSelectComponent,
    TagComponent,
    IsArrayPipe,
  ],
  templateUrl: './product-list-filters.component.html',
  styleUrl: './product-list-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListFiltersComponent implements OnInit, AfterViewInit {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  constructor() {
    // Added it, because appears console warning from angular about using this instead of [disabled]="isLoading"
    effect(() => {
      if (this.isSomeOptionsLoading()) {
        this.filtersForm.get('brandIds')?.disable();
        this.filtersForm.get('category')?.disable();
      } else {
        this.filtersForm.get('brandIds')?.enable();
        this.filtersForm.get('category')?.enable();
      }
    });
  }

  initialValue = input<FormState>();

  filtersChanged = output<FormState>();

  private readonly uuid = crypto.randomUUID();
  private readonly mobileOverlayTemplate = viewChild<TemplateRef<unknown>>('overlayTemplate');

  readonly isTabletOrLower = inject(BreakpointObserverService).isTabletOrLower;
  private readonly filterOptionsService = inject(FILTER_OPTIONS_SERVICE);
  private readonly overlayService = inject(OverlayService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly viewContainerRef = inject(ViewContainerRef);
  skeletonService = inject(SkeletonTemplateService);

  activeFilters = this.filterOptionsService.activeFilters;

  options: ProductListFilterOptionsSignal = {
    gender: this.filterOptionsService.genders,
    sortBy: this.filterOptionsService.sortBy,
    brandIds: this.filterOptionsService.brands,
    category: this.filterOptionsService.categories,
    color: signal([]),
    isAvailablePointsRange: signal(false),
  };

  loadingOptions = {
    isBrandsLoading: this.filterOptionsService.isBrandsLoading,
    isCategoriesLoading: this.filterOptionsService.isCategoriesLoading,
    isGendersLoading: this.filterOptionsService.isGendersLoading,
  };

  readonly openedMobileTemplate = signal<ProductListMobileTemplateType | null>(null);

  readonly isSortingActive = computed(() => this.activeFilters().includes('sortBy'));
  readonly isPointsRange = computed(() => this.activeFilters().includes('isAvailablePointsRange'));
  readonly hasBrandOptions = computed(() => this.filterOptionsService.brands()?.length > 0);

  initialLoading = this.filterOptionsService.initialLoading;
  isSomeOptionsLoading = computed(() => Object.values(this.loadingOptions).some((loading) => loading()));

  readonly skeletonConfig = {
    height: '30px',
    width: '100px',
    marginRight: '10px',
    marginBottom: '15px',
    'border-radius': '8px',
    border: '1px solid white',
  };

  categoriesMap = this.filterOptionsService.categoriesMap;
  brandsMap = this.filterOptionsService.brandsMap;
  filtersForm!: FormGroup<ProductListFiltersForm>;

  get brandsControl(): FormControl<string[] | number[]> {
    return this.filtersForm.controls.brandIds;
  }

  get categoryControl(): FormControl<string[] | number[]> {
    return this.filtersForm.controls.category;
  }

  get genderControl(): FormControl<string> {
    return this.filtersForm.controls.gender;
  }

  get colorControl(): FormControl<string> {
    return this.filtersForm.controls.color;
  }

  get sortingControl(): FormControl<string> {
    return this.filtersForm.controls.sortBy;
  }

  get isAvailablePointsRangeControl(): FormControl<boolean> {
    return this.filtersForm.controls.isAvailablePointsRange;
  }

  labelIdByFilter(control: FilterControlName): string {
    return `label_${ID_BY_FILTER[control]}_${this.uuid}`;
  }

  controlIdByFilter(control: FilterControlName): string {
    return `${ID_BY_FILTER[control]}_${this.uuid}`;
  }

  labelByFilter(control: FilterControlName): string {
    return LABEL_BY_FILTER[control].replace('{rewardName}', this.rewardName());
  }

  ariaLabelByControl(control: FilterControlName): string {
    return ARIA_LABEL_BY_FILTER[control].replace('{rewardName}', this.rewardName());
  }

  ngOnInit(): void {
    this.buildForm();
    // Need to refactor it for using query filters or remove at all
    this.setFormInitValue();
  }

  ngAfterViewInit(): void {
    this.detectFormChanges();
  }

  setFormInitValue(): void {
    if (this.initialValue()) {
      this.filtersForm.patchValue(this.initialValue() as any, { emitEvent: false });
    }
  }

  resetFilters(): void {
    this.filterOptionsService.resetSelectedFilters();
    this.filtersForm.patchValue({ category: [], brandIds: [] }, { emitEvent: false });
    this.filtersChanged.emit(this.filtersForm.getRawValue());
  }

  clearForm(): void {
    this.filtersForm.patchValue(DEFAULT_FILTERS_FORM_STATE, { emitEvent: false });
  }

  removeSelectedOptions(control: FormControl<any>, selectedOption: any): void {
    control.setValue(
      Array.isArray(control.value) ? control.value.filter((option: any) => option !== selectedOption) : null,
    );
    this.emitFormValue();
  }

  onCloseMobileMenu(): void {
    this.overlayService.close();
  }

  onOpenMobileSelect(type: ProductListMobileTemplateType): void {
    this.openedMobileTemplate.set(type);
    this.onOpenMobileMenu();
  }

  onApplyFilters(): void {
    this.filtersChanged.emit(this.filtersForm.getRawValue());
    this.onCloseMobileMenu();
  }

  getSelectedLength(control: FormControl<string[] | number[]>): number {
    return control?.value?.filter?.(Boolean).length || 0;
  }

  private onOpenMobileMenu(): void {
    this.overlayService.open(this.mobileOverlayTemplate()!, this.viewContainerRef);
  }

  private detectFormChanges(): void {
    if (this.isTabletOrLower()) {
      this.bindMobileFormChangesToOutput();
    } else {
      this.bindFormChangesToOutput();
    }
  }

  private bindFormChangesToOutput(): void {
    merge(this.sortingControl.valueChanges, this.isAvailablePointsRangeControl.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.emitFormValue();
      });
  }

  private bindMobileFormChangesToOutput(): void {
    if (!this.isAvailablePointsRangeControl) return;

    this.isAvailablePointsRangeControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.emitFormValue();
    });
  }

  emitFormValue(): void {
    const { brandIds, gender, sortBy, category, color, isAvailablePointsRange } = this.filtersForm.getRawValue();

    // Rewrite it to avoid undefined in array, because apply button implemented as option, so it could be wrote as undefined
    const brands = brandIds?.filter(Boolean);
    const categories = category?.filter(Boolean);

    this.filtersChanged.emit({
      ...(brandIds && { brandIds: brands }),
      ...(gender && { gender }),
      ...(sortBy && { sortBy }),
      ...(category && { category: categories }),
      ...(color && { color }),
      ...(isAvailablePointsRange
        ? {
            isAvailablePointsRange,
          }
        : {}),
    } as FormState);
  }

  private buildForm(): void {
    this.filtersForm = this.fb.group(
      this.activeFilters().reduce(
        (acc, filter) => {
          acc[filter] = new FormControl(null);
          return acc;
        },
        {} as Record<FilterControlName, FormControl>,
      ),
    );
  }
}
